import {useState, useEffect, useRef} from 'react';

import classnames from 'classnames';

import {ThreadType} from 'nutshell-graphql-types';

import {TextField} from '../../../textfield/textfield';

import type {Recipient} from '../../types';

import {CharacterLimitPopover} from './character-limit-popover';
import {CollapsedMessageInputRow} from './collapsed-message-input-row';
import {PlainTextInputFooter} from './plain-text-input-footer';
import {SendButton} from './send-button';
import type {SendInput, ImageHandlingProps} from './types';

import collapsedStyles from './collapsed-message-input-row.module.css';
import styles from './message-input.module.css';

const CHARACTER_LIMIT = 255;
const MESSAGE_INPUT_ID = 'message-input';

interface Props extends ImageHandlingProps {
    onSend: (input: {
        content: string;
        snoozeDuration?: number;
        closeThread?: boolean;
        imageUrls?: string[];
    }) => Promise<any>;
    isSending: boolean;
    showSnoozeOptions?: boolean;
    cantSendMessage: string | null | undefined;
    shouldHideControls: boolean;
    threadType?: ThreadType;
    recipient: Recipient | null | undefined;
    placeholder: string;
    notifyTyping: () => void;
    // @ts-expect-error
    handleFilesUploaded: ({filesUploaded}) => void;
}

export function PlainTextMessageInput(props: Props) {
    const [value, setValue] = useState('');
    const [isMessageSending, setIsMessageSending] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const charaterLimitRef = useRef<HTMLDivElement | null | undefined>(null);
    const textFieldRef = useRef<HTMLInputElement | null | undefined>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const recipientId = props.recipient?.id;

    useEffect(() => {
        if (recipientId && textFieldRef.current) {
            textFieldRef.current.focus();
        }
    }, [recipientId]);

    // Delay focus to prevent load issues in the sidebar
    useEffect(() => {
        if (isExpanded) {
            setTimeout(() => {
                document.getElementById(MESSAGE_INPUT_ID)?.focus();
            }, 500);
        }
    }, [isExpanded]);

    useEffect(() => {
        if (props.cantSendMessage) {
            setValue('');
        }
    }, [props.cantSendMessage]);

    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        const handleMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsExpanded(false);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isExpanded]);

    const shouldEnforceCharacterLimit = props.threadType === ThreadType.SMS;
    const isOverCharacterLimit = shouldEnforceCharacterLimit && value.length > CHARACTER_LIMIT;
    const hasValidContent = value.trim().length > 0;
    const isSendDisabled =
        (!hasValidContent && !props.hasImages) || isOverCharacterLimit || isMessageSending;
    const isInputDisabled = Boolean(props.cantSendMessage || isMessageSending);

    const handleSend = async (input?: SendInput | null | undefined) => {
        setIsMessageSending(true);
        try {
            const allImageUrls = await props.collectAllImageUrls();
            await props.onSend({
                content: value,
                ...input,
                imageUrls: allImageUrls.length > 0 ? allImageUrls : undefined,
            });
            setValue('');
            setIsExpanded(false);
            props.clearAllImages();
        } finally {
            setIsMessageSending(false);
            document.getElementById(MESSAGE_INPUT_ID)?.focus();
        }
    };

    const handleChange = (val: string) => {
        setValue(val);
        props.notifyTyping();
    };

    const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.shiftKey) return;
        e.preventDefault();
        if (!isSendDisabled) {
            handleSend();
        }
    };

    if (!isExpanded) {
        return (
            <div ref={containerRef} className={collapsedStyles['collapsed-container']}>
                <CollapsedMessageInputRow
                    previewText={value}
                    placeholder={props.placeholder}
                    onExpand={() => setIsExpanded(true)}
                    footer={
                        !props.shouldHideControls ? (
                            <SendButton
                                isDisabled={isSendDisabled}
                                onSend={handleSend}
                                isSending={props.isSending}
                                showSnoozeOptions={props.showSnoozeOptions}
                            />
                        ) : undefined
                    }
                />
            </div>
        );
    }

    return (
        <div ref={containerRef}>
            <TextField
                id={MESSAGE_INPUT_ID}
                textFieldRef={textFieldRef}
                value={value}
                extraPadding={true}
                onChange={handleChange}
                multiline={true}
                placeholder={props.placeholder}
                onEnter={onEnter}
                // @ts-expect-error
                disabled={isInputDisabled}
                focusOnMount={true}
                selectOnFocus={true}
                containerClassName={classnames({
                    [styles['input-container']]: props.hasImages,
                })}
                customFooter={
                    props.shouldHideControls ? undefined : (
                        <PlainTextInputFooter
                            disabled={isInputDisabled}
                            isSendDisabled={isSendDisabled}
                            onSend={handleSend}
                            isSending={props.isSending}
                            showSnoozeOptions={props.showSnoozeOptions}
                            shouldEnforceCharacterLimit={shouldEnforceCharacterLimit}
                            isOverCharacterLimit={isOverCharacterLimit}
                            characterCount={value.length}
                            characterLimit={CHARACTER_LIMIT}
                            charaterLimitRef={charaterLimitRef}
                            onFilesUploaded={props.handleFilesUploaded}
                        />
                    )
                }
            />
            <CharacterLimitPopover
                anchorRef={charaterLimitRef}
                isOverLimit={isOverCharacterLimit}
                characterLimit={CHARACTER_LIMIT}
            />
        </div>
    );
}
